import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import {
  DollarSign,
  Droplets,
  CheckCircle2,   // (kept in case future variants surface elsewhere)
  CreditCard,
  Zap,
  ChevronDown,
  ChevronUp,
  Download,
  FileText,
} from 'lucide-react';

import { formatDate } from '../lib/datetime';

const MONTH_NAMES_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// One consolidated statement card per (resident, month, year). Groups all
// invoice types (maintenance, water, common water, common electricity) so a
// resident sees a single monthly bill instead of 4 separate cards.
//
// Extracted from `pages/Invoices.js` in v2.38 as part of the Wave 2 refactor.
// Behavior is unchanged — all props, test-ids, and DOM output are identical
// to the previous inline version.
export const MonthlyStatementCard = ({ statement, onPayOnline, onDownloadPdf, onSubmitPayment }) => {
  // silence eslint on the imported-but-unused icon (kept for future usage)
  void CheckCircle2;
  const [open, setOpen] = useState(false);
  const period = `${MONTH_NAMES_FULL[statement.period_month - 1]} ${statement.period_year}`;
  const allPaid = statement.all_paid;
  const unpaidCount = statement.line_items.filter(l => l.status !== 'paid').length;
  const creditBalance = Number(statement.credit_balance || 0);
  const hasCredit = creditBalance > 0.005;

  const iconFor = (type) =>
    type === 'common_electricity' ? <Zap className="h-4 w-4 text-amber-600" /> :
    type === 'common_water' ? <Droplets className="h-4 w-4 text-teal-600" /> :
    type === 'water' ? <Droplets className="h-4 w-4 text-cyan-600" /> :
    <DollarSign className="h-4 w-4 text-blue-600" />;

  const detailFor = (li) => {
    const parts = [];
    if (li.usage !== null && li.usage !== undefined) {
      parts.push(`${li.previous_reading}→${li.current_reading} · ${li.usage} units`);
    }
    if (li.share_percent !== null && li.share_percent !== undefined) {
      parts.push(`share ${Number(li.share_percent).toFixed(4)}%`);
    }
    if (li.total_property_bill !== null && li.total_property_bill !== undefined) {
      parts.push(`of LKR ${Number(li.total_property_bill).toLocaleString(undefined, { minimumFractionDigits: 2 })}`);
    }
    if (li.vat_percent && li.vat_amount) {
      parts.push(`VAT@${li.vat_percent}% (LKR ${Number(li.vat_amount).toFixed(2)})`);
    }
    if (li.notes) parts.push(li.notes);
    return parts.join(' · ');
  };

  return (
    <Card className="bg-white border border-[#e2e8f0] hover:shadow-sm transition-shadow" data-testid={`statement-card-${statement.period_year}-${statement.period_month}`}>
      {/* Header — always visible */}
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full text-left p-5 flex items-start justify-between gap-4"
        data-testid={`statement-toggle-${statement.period_year}-${statement.period_month}`}
      >
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="h-10 w-10 rounded-md bg-[#f1f5f9] flex items-center justify-center flex-shrink-0">
            <FileText className="h-5 w-5 text-[#0f172a]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-base font-semibold" style={{ fontFamily: 'Outfit' }}>Monthly Statement</h3>
              <span className="text-xs text-[#64748b]">•</span>
              <span className="text-xs text-[#64748b]">{period}</span>
            </div>
            <p className="text-xs text-[#64748b]">
              {statement.line_item_count} item{statement.line_item_count !== 1 ? 's' : ''}
              {statement.unit_number ? ` · Unit ${statement.unit_number}` : ''}
              {statement.property_name ? ` · ${statement.property_name}` : ''}
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                allPaid ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
              }`}>
                {allPaid ? 'All Paid' : `${unpaidCount} Unpaid`}
              </span>
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-[#f1f5f9] text-[#334155]">
                Total LKR {Number(statement.total).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
              {!allPaid && (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-50 text-red-700" data-testid={`statement-outstanding-${statement.period_year}-${statement.period_month}`}>
                  Outstanding LKR {Number(statement.outstanding).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
              )}
              {statement.surcharge_total > 0.005 && (
                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800" data-testid={`statement-surcharge-badge-${statement.period_year}-${statement.period_month}`}>
                  ⚠ {statement.overdue_count} overdue · +LKR {Number(statement.surcharge_total).toLocaleString(undefined, { minimumFractionDigits: 2 })} late fee
                </span>
              )}
              {hasCredit && (
                <span
                  className="px-2 py-0.5 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-800"
                  data-testid={`statement-credit-badge-${statement.period_year}-${statement.period_month}`}
                  title="Overpayment carried forward — auto-applied to future invoices"
                >
                  Credit LKR {creditBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })} available
                </span>
              )}
            </div>
          </div>
        </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <p className="text-2xl font-semibold text-right" style={{ fontFamily: 'Outfit' }} data-testid={`statement-grand-total-${statement.period_year}-${statement.period_month}`}>
              LKR {Number(statement.grand_total_due != null ? statement.grand_total_due : (statement.outstanding > 0.005 ? statement.outstanding : statement.total)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            {open ? <ChevronUp className="h-5 w-5 text-[#64748b]" /> : <ChevronDown className="h-5 w-5 text-[#64748b]" />}
          </div>
      </button>

      {/* Expanded — line items + actions */}
      {open && (
        <div className="border-t border-[#e2e8f0]">
          <div className="p-5 space-y-2">
            {statement.line_items.map(li => (
              <div key={li.invoice_id} className="flex items-start justify-between gap-3 py-2 border-b border-[#e2e8f0] last:border-b-0" data-testid={`statement-line-${li.invoice_id}`}>
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="h-8 w-8 rounded-md bg-[#f8fafc] flex items-center justify-center flex-shrink-0">
                    {iconFor(li.invoice_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium">{li.label}</p>
                      {li.invoice_number && (
                        <span
                          className="px-1.5 py-0.5 text-[10px] font-mono font-semibold rounded bg-slate-100 text-slate-700 border border-slate-200"
                          data-testid={`statement-line-number-${li.invoice_id}`}
                          title="Invoice number"
                        >
                          {li.invoice_number}
                        </span>
                      )}
                    </div>
                    {detailFor(li) && (
                      <p className="text-xs text-[#64748b] mt-0.5">{detailFor(li)}</p>
                    )}
                    <span className={`inline-block mt-1 px-2 py-0.5 text-[10px] font-medium rounded-full ${
                      li.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {li.status === 'paid' ? (li.paid_via ? `Paid via ${li.paid_via}` : 'Paid') : 'Unpaid'}
                    </span>
                    {li.due_date && (
                      <span className={`inline-block mt-1 ml-1 px-2 py-0.5 text-[10px] font-medium rounded-full ${
                        li.is_overdue ? 'bg-red-100 text-red-800' : 'bg-[#f1f5f9] text-[#64748b]'
                      }`}>
                        {li.is_overdue ? 'Overdue since ' : 'Due '}
                        {formatDate(li.due_date)}
                      </span>
                    )}
                    {li.surcharge_amount > 0.005 && (
                      <span className="inline-block mt-1 ml-1 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-red-100 text-red-800" data-testid={`statement-line-surcharge-${li.invoice_id}`}>
                        +LKR {Number(li.surcharge_amount).toFixed(2)} late fee (3%)
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <p className="text-sm font-semibold">LKR {Number(li.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                  {li.surcharge_amount > 0.005 && (
                    <p className="text-[10px] text-red-700">Total due LKR {Number(li.total_due).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                  )}
                  {li.status !== 'paid' && (
                    <div className="flex gap-1">
                      <Button
                        onClick={() => onPayOnline({ _id: li.invoice_id, amount: li.total_due || li.amount })}
                        size="sm"
                        className="h-7 text-xs bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
                        data-testid={`statement-pay-${li.invoice_id}`}
                      >
                        <CreditCard className="mr-1 h-3 w-3" /> Pay
                      </Button>
                      {onSubmitPayment && (
                        <Button
                          onClick={() => onSubmitPayment({
                            _id: li.invoice_id,
                            amount: li.amount,
                            total_due: li.total_due || li.amount,
                            balance: li.balance != null ? li.balance : (li.total_due || li.amount) - (li.paid_amount || 0),
                          })}
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs border-[#e2e8f0]"
                          data-testid={`statement-submit-${li.invoice_id}`}
                        >
                          Log
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 p-5 pt-0 border-t border-[#e2e8f0] bg-[#fafafa] rounded-b-lg">
            <div className="text-sm">
              <span className="text-[#64748b]">Subtotal:</span>{' '}
              <span className="font-semibold">LKR {Number(statement.total).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              {statement.paid > 0.005 && (
                <>
                  <span className="text-[#64748b] ml-3">Paid:</span>{' '}
                  <span className="text-green-700">−LKR {Number(statement.paid).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </>
              )}
              {statement.surcharge_total > 0.005 && (
                <>
                  <span className="text-[#64748b] ml-3">Late fee ({statement.surcharge_rate_percent}%):</span>{' '}
                  <span className="text-red-700 font-medium" data-testid={`statement-surcharge-total-${statement.period_year}-${statement.period_month}`}>+LKR {Number(statement.surcharge_total).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </>
              )}
              {hasCredit && (
                <>
                  <span className="text-[#64748b] ml-3">Credit balance (available):</span>{' '}
                  <span
                    className="text-emerald-700 font-medium"
                    data-testid={`statement-credit-balance-${statement.period_year}-${statement.period_month}`}
                    title="Overpayment carried forward — auto-applied to future invoices"
                  >
                    LKR {creditBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </>
              )}
              {!allPaid && (
                <>
                  <span className="text-[#64748b] ml-3">Amount Due:</span>{' '}
                  <span className="text-red-700 font-semibold">LKR {Number(statement.grand_total_due).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onDownloadPdf(statement)}
              className="text-[#0f172a] border-[#e2e8f0]"
              data-testid={`statement-download-pdf-${statement.period_year}-${statement.period_month}`}
            >
              <Download className="mr-1 h-4 w-4" /> Download PDF
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default MonthlyStatementCard;
